/**
 * CohortChart — the "AVG Customer Value" cohort chart (founder 2026-06-15,
 * matching the Praxis reference). Each x-slot is the month a set of customers
 * first bought; grouped bars are their average value at each timeframe
 * (1/30/60/90/180 days); the line is the cohort's customer count.
 *
 * Cohort-based + maturity-gated: a cohort only shows a bar for a timeframe once
 * the WHOLE cohort has had that many days to mature (immature cells arrive null
 * from the backend), so recent months drop off for the longer windows. HOVER a
 * legend item to isolate it — exactly like Praxis (hover "90 Days" → only the
 * 90-day bars; hover "Customers" → only the count line). Hover a bar for stats.
 */

import { useRef, useState } from "react";

interface Cell { averageLtvBase: number; cohortSize: number }
export interface CohortChartRow { cohortKey: string; cohortSize: number; cellsByTimeframe: Record<number, Cell> }

const money0 = (n: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function tfLabel(d: number): string {
  if (d === 1) return "1 Day";
  if (d === 180) return "6 Months";
  if (d === 365) return "1 Year";
  if (d % 365 === 0) return `${d / 365} Years`;
  return `${d} Days`;
}

/** Shade per timeframe index — light→dark accent, like the reference's blues. */
const tfColor = (i: number, n: number): string =>
  `color-mix(in srgb, var(--accent) ${Math.round(34 + (i / Math.max(1, n - 1)) * 52)}%, var(--surface))`;

type Series = number | "count" | null;

export function CohortChart({ rows, timeframes, mode = "bar" }: {
  rows: CohortChartRow[];
  timeframes: number[];
  mode?: "bar" | "line";
}): React.ReactElement {
  // Hovering a legend item isolates that series (a timeframe, or the count
  // line). null = show everything.
  const [iso, setIso] = useState<Series>(null);
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  if (rows.length === 0) return <div style={{ color: "var(--faint)", padding: "24px 0" }}>No cohorts yet.</div>;

  const activeTfs = typeof iso === "number" ? [iso] : iso === "count" ? [] : timeframes;
  const showLine = iso === null || iso === "count";
  const W = 940, H = 320, padL = 46, padR = 48, padB = 40, padT = 16;
  const iw = W - padL - padR, ih = H - padB - padT;

  const matureV = (r: CohortChartRow, t: number): number | null => {
    const c = r.cellsByTimeframe[t];
    return c && c.cohortSize > 0 ? c.averageLtvBase : null;
  };
  const maxV = Math.max(60, ...rows.flatMap((r) => activeTfs.map((t) => matureV(r, t) ?? 0))) * 1.1;
  const maxC = Math.max(1, ...rows.map((r) => r.cohortSize));
  const band = iw / rows.length;
  const x0 = (i: number): number => padL + (i + 0.5) * band;
  const yV = (v: number): number => padT + ih - (v / maxV) * ih;
  const yC = (c: number): number => padT + ih - (c / maxC) * ih;

  const onMove = (e: React.MouseEvent<SVGSVGElement>): void => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.floor((px - padL) / band);
    setHover(idx >= 0 && idx < rows.length ? idx : null);
  };

  const gw = band * 0.74, bw = Math.max(2, gw / Math.max(1, activeTfs.length));
  const hoveredRow = hover !== null ? rows[hover] : null;
  // With all history (many months) the x-labels would smear — thin them to ~16.
  const labelStep = Math.max(1, Math.ceil(rows.length / 16));

  return (
    <div style={{ position: "relative" }}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" onMouseMove={onMove} onMouseLeave={() => setHover(null)} style={{ display: "block", cursor: "crosshair" }}>
        {/* $ grid + left axis */}
        {[0, maxV / 3, (maxV * 2) / 3, maxV].map((v, i) => (
          <g key={i}>
            <line x1={padL} y1={yV(v)} x2={W - padR} y2={yV(v)} stroke="var(--grid)" />
            <text x={6} y={yV(v) + 3} fill="var(--body-faint)" fontSize={10.5} className="dd-num">{money0(v)}</text>
          </g>
        ))}
        {/* right axis (customer count) */}
        {showLine && [0, maxC].map((c, i) => (
          <text key={i} x={W - padR + 6} y={yC(c) + 3} fill="var(--accent-2)" fontSize={10} className="dd-num">{Math.round(c).toLocaleString()}</text>
        ))}
        {/* hover band highlight */}
        {hover !== null && <rect x={x0(hover) - band / 2} y={padT} width={band} height={ih} fill="var(--accent)" opacity={0.06} />}
        {/* x labels — thinned when there are many months so they stay legible */}
        {rows.map((r, i) => (i % labelStep === 0
          ? <text key={r.cohortKey} x={x0(i)} y={H - 14} textAnchor="middle" fill="var(--body-faint)" fontSize={10.5}>{r.cohortKey}</text>
          : null))}
        {/* series */}
        {mode === "bar"
          ? rows.map((r, i) => (
              <g key={r.cohortKey}>
                {activeTfs.map((t, bi) => {
                  const v = matureV(r, t);
                  if (v === null) return null;
                  const gx = x0(i) - gw / 2 + bi * bw;
                  return <rect key={t} x={gx.toFixed(1)} y={yV(v).toFixed(1)} width={(bw - 1.5).toFixed(1)} height={(padT + ih - yV(v)).toFixed(1)} rx={2} fill={typeof iso === "number" ? "var(--accent)" : tfColor(timeframes.indexOf(t), timeframes.length)} />;
                })}
              </g>
            ))
          : activeTfs.map((t) => {
              const pts = rows.map((r, i) => { const v = matureV(r, t); return v === null ? null : `${x0(i).toFixed(1)},${yV(v).toFixed(1)}`; }).filter((p): p is string => p !== null);
              return pts.length >= 2 ? <path key={t} d={`M${pts.join(" L")}`} fill="none" stroke={typeof iso === "number" ? "var(--accent)" : tfColor(timeframes.indexOf(t), timeframes.length)} strokeWidth={2.2} /> : null;
            })}
        {/* customer-count line */}
        {showLine && (() => {
          const pts = rows.map((r, i) => `${x0(i).toFixed(1)},${yC(r.cohortSize).toFixed(1)}`);
          return <>
            <path d={`M${pts.join(" L")}`} fill="none" stroke="var(--accent-2)" strokeWidth={2} strokeDasharray={mode === "line" ? "4 3" : undefined} />
            {rows.map((r, i) => <circle key={r.cohortKey} cx={x0(i)} cy={yC(r.cohortSize)} r={2.4} fill="var(--accent-2)" />)}
          </>;
        })()}
        {hover !== null && <line x1={x0(hover)} y1={padT} x2={x0(hover)} y2={padT + ih} stroke="var(--body-faint)" strokeDasharray="3 3" opacity={0.5} />}
      </svg>

      {/* hover tooltip */}
      {hoveredRow && (
        <div style={{ position: "absolute", top: 4, left: `${(Math.min(rows.length - 1, hover!) / rows.length) * 100}%`, transform: hover! > rows.length / 2 ? "translateX(-105%)" : "translateX(8px)", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-pop)", padding: "8px 10px", fontSize: 11.5, pointerEvents: "none", minWidth: 150, zIndex: 3 }}>
          <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>{hoveredRow.cohortKey}</div>
          <div style={{ color: "var(--body)", marginBottom: 5 }} className="dd-num">{hoveredRow.cohortSize.toLocaleString()} customers</div>
          {(typeof iso === "number" ? [iso] : timeframes).map((t) => {
            const v = matureV(hoveredRow, t);
            return (
              <div key={t} style={{ display: "flex", justifyContent: "space-between", gap: 14, color: "var(--body)" }}>
                <span>{tfLabel(t)}</span>
                <span className="dd-num" style={{ color: v === null ? "var(--faint)" : "var(--num)", fontWeight: 600 }}>{v === null ? "—" : money0(v)}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* legend — HOVER a series to isolate it (Praxis behavior) */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
        <button onMouseEnter={() => setIso("count")} onMouseLeave={() => setIso(null)}
          style={legendChip(iso === null || iso === "count")}>
          <i style={{ width: 12, height: 2, background: "var(--accent-2)" }} />
          Customers
        </button>
        {timeframes.map((t, i) => (
          <button key={t} onMouseEnter={() => setIso(t)} onMouseLeave={() => setIso(null)}
            style={legendChip(iso === null || iso === t)}>
            <i style={{ width: 9, height: 9, borderRadius: 2, background: tfColor(i, timeframes.length) }} />
            {tfLabel(t)}
          </button>
        ))}
        <span style={{ fontSize: 11, color: "var(--body-faint)" }}>Hover a label to isolate it.</span>
      </div>
    </div>
  );
}

function legendChip(on: boolean): React.CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 6,
    border: "1px solid var(--border)", background: "var(--surface)",
    color: on ? "var(--text)" : "var(--faint)", borderRadius: 999, padding: "4px 10px",
    fontSize: 11.5, fontWeight: 500, cursor: "default", opacity: on ? 1 : 0.5,
  };
}
